import React from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';

/**
 * Role-based route protection component
 * Wraps routes that require specific roles to access
 *
 * @param {React.ReactNode} children - The component to render if authorized
 * @param {string[]} allowedRoles - Array of roles that can access this route
 * @param {boolean} requireAll - If true, user must have ALL roles; if false, ANY role suffices
 */
const RoleBasedRoute = ({ children, allowedRoles = [], requireAll = false }) => {
  const { isAuthenticated, isLoading, hasRole, hasAnyRole, userRoles } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner fullScreen tip="Checking permissions..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If no roles specified, allow any authenticated user
  if (allowedRoles.length === 0) {
    return children;
  }

  // Check role requirements
  const hasAccess = requireAll
    ? allowedRoles.every(role => hasRole(role))
    : hasAnyRole(allowedRoles);

  // Redirect to unauthorized page if user lacks required roles
  if (!hasAccess) {
    console.warn(`Access denied. User roles: [${userRoles.join(', ')}], Required: [${allowedRoles.join(', ')}]`);
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

RoleBasedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
};

RoleBasedRoute.defaultProps = {
  allowedRoles: [],
  requireAll: false,
};

export default RoleBasedRoute;
